/**
 * App-wide currency model.
 *
 * The database stores money in **NGN only** — single source of truth for
 * accounting, designer payouts, and dispute reconciliation. Conversion to
 * the user's preferred currency happens at *display time* only, using the
 * latest FX rate served by the backend.
 *
 * Two currencies supported in the MVP:
 *   - NGN — Nigerian Naira (₦)  — the storage currency
 *   - USD — US Dollar       ($)  — the default display currency for non-NG users
 */

export type CurrencyCode = 'NGN' | 'USD';

export interface CurrencyMeta {
  code: CurrencyCode;
  symbol: string;
  /** Used for `toLocaleString` so 1,234.56 vs 1.234,56 etc. */
  locale: string;
  /** Decimal places shown by default. */
  decimals: number;
  name: string;
}

export const CURRENCIES: Record<CurrencyCode, CurrencyMeta> = {
  NGN: { code: 'NGN', symbol: '₦', locale: 'en-NG', decimals: 0, name: 'Nigerian Naira' },
  USD: { code: 'USD', symbol: '$', locale: 'en-US', decimals: 2, name: 'US Dollar' },
};

export const SUPPORTED_CURRENCIES: CurrencyCode[] = ['NGN', 'USD'];

/**
 * FX rate snapshot — how many of `target` you get for 1 unit of `base`.
 * Always served base-NGN so every conversion is a single multiply.
 */
export interface FxRateSnapshot {
  base: 'NGN';
  /** NGN_per_USD: e.g. 1500 means 1 USD = 1500 NGN. */
  ngnPerUsd: number;
  /** When the rate was last refreshed (ISO string). */
  fetchedAtISO: string;
  /** Optional human source label, e.g. "open.er-api.com" */
  source?: string;
}

/**
 * Admin-controlled currency settings.
 */
export interface CurrencySettings {
  defaultCurrency: CurrencyCode;
  rate: FxRateSnapshot;
}

/**
 * Convert an NGN amount into the given target currency using a snapshot.
 * Returns the converted value rounded to the target currency's decimals.
 */
export function convertFromNgn(amountNgn: number, target: CurrencyCode, rate: FxRateSnapshot): number {
  if (!Number.isFinite(amountNgn)) return 0;
  if (target === 'NGN') return Math.round(amountNgn);
  if (target === 'USD') {
    if (!rate?.ngnPerUsd || rate.ngnPerUsd <= 0) return 0;
    const usd = amountNgn / rate.ngnPerUsd;
    return Math.round(usd * 100) / 100;
  }
  return amountNgn;
}

/**
 * Format an NGN amount as a string in the given currency.
 *
 * Examples:
 *   formatInCurrency(520000, 'NGN', rate) → "₦520,000"
 *   formatInCurrency(520000, 'USD', rate) → "$346.67"
 */
export function formatInCurrency(
  amountNgn: number | null | undefined,
  target: CurrencyCode,
  rate: FxRateSnapshot,
  opts?: { showCode?: boolean },
): string {
  const meta = CURRENCIES[target];
  if (amountNgn == null || Number.isNaN(Number(amountNgn))) {
    return `${meta.symbol}0`;
  }
  const value = convertFromNgn(Number(amountNgn), target, rate);
  const str = value.toLocaleString(meta.locale, {
    minimumFractionDigits: meta.decimals,
    maximumFractionDigits: meta.decimals,
  });
  return opts?.showCode ? `${meta.symbol}${str} ${meta.code}` : `${meta.symbol}${str}`;
}

/**
 * Best-effort initial currency detection based on browser locale. Returns
 * USD by default — admin override + user override layer on top of this.
 */
export function detectCurrencyFromLocale(): CurrencyCode {
  if (typeof navigator === 'undefined') return 'USD';
  const lang = (navigator.language || '').toLowerCase();
  if (lang === 'en-ng' || lang.endsWith('-ng') || lang === 'ig' || lang === 'yo' || lang === 'ha') {
    return 'NGN';
  }
  return 'USD';
}

/**
 * Map an ISO 3166-1 alpha-2 country code to the display currency we want
 * to default to. Nigeria → NGN, everywhere else → USD.
 */
export function currencyForCountry(countryCode: string | null | undefined): CurrencyCode {
  if (!countryCode) return 'USD';
  return countryCode.toUpperCase() === 'NG' ? 'NGN' : 'USD';
}

/**
 * Stable fallback rate used when the backend hasn't yet responded — keeps
 * the UI usable on first paint. Will be replaced by the real rate as soon
 * as the fetch resolves.
 */
export const FALLBACK_RATE: FxRateSnapshot = {
  base: 'NGN',
  ngnPerUsd: 1600,
  fetchedAtISO: new Date().toISOString(),
  source: 'fallback',
};
